<script setup lang="ts">
const props = defineProps({
  object: {
    type: Object as PropType<Sanity.Default.Schema.Gallery>,
    required: true,
  },
})

const { urlFor } = useSanityImage()

const aspectRatio: ComputedRef<string> = computed(() => {
  if (props.object?.type === 'landscape') {
    return 'aspect-[4/3]'
  }
  if (props.object?.type === 'square') {
    return 'aspect-[1/1]'
  }
  if (props.object?.type === 'portrait') {
    return 'aspect-[3/4]'
  }
  return ''
})

const aspectRatioMultiplier: ComputedRef<number> = computed(() => {
  if (props.object?.type === 'landscape') {
    return 3 / 4
  }
  if (props.object?.type === 'square') {
    return 1
  }
  if (props.object?.type === 'portrait') {
    return 4 / 3
  }
  return 1
})

const imageWidth: ComputedRef<number> = computed(() => {
  if (!props.object?.images) {
    return 0
  }
  if (props.object?.images.length === 1) {
    return 1600
  }
  if (props.object?.images.length === 2) {
    return 800
  }
  if (props.object?.images.length % 3 === 0) {
    return 530
  }
  return 352
})

const imageHeight: ComputedRef<number> = computed(() => {
  if (!props.object?.images) {
    return 0
  }
  if (props.object?.images.length === 1) {
    return imageWidth.value * aspectRatioMultiplier.value
  }
  if (props.object?.images.length === 2) {
    return imageWidth.value * aspectRatioMultiplier.value
  }
  if (props.object?.images.length % 3 === 0) {
    return imageWidth.value
  }
  return imageWidth.value * aspectRatioMultiplier.value
})

const gridCols: ComputedRef<string> = computed(() => {
  if (!props.object?.images) {
    return ''
  }
  if (props.object?.images.length === 1) {
    return 'lg:grid-cols-4 lg:grid-cols-4 lg:gap-12 xl:gap-20'
  }
  if (props.object?.images.length === 2) {
    return 'grid-cols-2 gap-3 lg:gap-8'
  }
  if (props.object?.images.length % 3 === 0) {
    return 'grid-cols-3 gap-3 lg:gap-6'
  }
  return 'grid-cols-4 gap-2 lg:gap-4'
})

const colspan = computed(() => {
  if (props.object?.images.length === 1) {
    return 'lg:col-span-3 lg:max-w-3xl'
  }
  return ''
})
</script>

<template>
  <div v-if="object" class="container-8xl pb-12">
    <div
      v-if="object.eyebrow || object.heading || object.body"
      class="gallery-body max-w-3xl mb-4"
    >
      <div v-if="object.eyebrow" class="eyebrow">
        {{ object.eyebrow }}
      </div>
      <h3 v-if="object.heading" class="thin-title">
        {{ object.heading }}
      </h3>
      <div v-if="object.body">
        <SanityContentWrapper
          :blocks="object.body"
        />
      </div>
    </div>
    <div
      v-if="object.images && object.images.length"
      class="grid gap-2 lg:gap-8 overflow-hidden"
      :class="gridCols"
    >
      <template v-if="object.images.length === 1">
        <div />
      </template>
      <nuxt-img
        v-for="image in object.images"
        :key="image.asset._key"
        :src="urlFor(image.asset).width(imageWidth).height(imageHeight).fit('crop').url()"
        :alt="image.alt"
        class="relative object-cover object-center w-full h-full"
        :class="`${aspectRatio} ${colspan}`"
        quality="85"
        :width="imageWidth"
        :height="imageHeight"
        loading="lazy"
      />
    </div>
  </div>
</template>
